import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import Link from 'next/link';
import { useBasePath } from '../../../lib/use-base-path';
import { Spinner } from '../../Spinner';
import styles from './IconButton.module.css';
import skins from './buttonSkins.module.css';

// THE icon-only button — Figma "⚠️ Symphony DS" → Icon Button (node 319:228).
// Never hand-roll one; see CLAUDE.md → "Buttons".
//
// The Button's square sibling: same Skin × Type matrix over the same size scale,
// so the props mean exactly what they mean on Button and the colours come from the
// shared buttonSkins module. An `aria-label` is required since there is no text.
//
// Polymorphic: pass `href` and it renders a same-origin Next <Link> (with the
// active mount prefix) instead of <button> — same visual, navigates on click.
// Pass `linkAs` to override the displayed URL (like Next's `as` prop).
//
// `decorative` renders the box as an `aria-hidden` <span> instead: the DS icon-button
// SHAPE with no control semantics, for a glyph inside a surface that is ALREADY the hit
// target (a `<Card component="button">` row and its trailing chevron). Nesting a real
// <button> in a <button> is invalid HTML and steals the card's click, so the trailing
// affordance has to be a span — but it should still be the designed 36px ghost box
// rather than a bare SVG. Same call Card's `component` prop exists for. It takes no
// `aria-label` (there is nothing to label — the card names itself) and no handlers;
// drive its hover from the parent (`.card:hover .chevron`), since the pointer is over
// the card, not always over the glyph.
export type IconButtonSize = 'tiny' | 'small' | 'medium' | 'large';
export type IconButtonVariant = 'contained' | 'outlined' | 'text';
export type IconButtonColor = 'standard' | 'accent' | 'destructive';

type CommonProps = {
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  color?: IconButtonColor;
  loading?: boolean;
  children: ReactNode;
  className?: string;
};

type BaseProps = CommonProps & { 'aria-label': string };

type AsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | keyof BaseProps> & { href?: undefined; decorative?: undefined };
type AsLink = BaseProps & {
  href: string;
  linkAs?: string;
  onClick?: () => void;
  target?: string;
  rel?: string;
  'aria-current'?: 'page' | 'true' | 'false' | boolean;
  decorative?: undefined;
};
type AsDecorative = CommonProps & { decorative: true; href?: undefined; 'aria-label'?: undefined };

export type IconButtonProps = AsButton | AsLink | AsDecorative;

export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(
  function IconButton(props, ref) {
    const basePath = useBasePath();
    const { size = 'medium', variant = 'text', color = 'standard', loading, children, className } = props;
    const classes = [
      styles.root,
      styles[`size-${size}`],
      // Skin × Type colours are shared with Button — see buttonSkins.module.css.
      skins[`${variant}-${color}`],
      loading && styles.loading,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const content = loading ? <Spinner /> : children;

    if (props.decorative) {
      return (
        <span aria-hidden className={classes}>
          {content}
        </span>
      );
    }

    if (props.href !== undefined) {
      const { href, linkAs, onClick, target, rel, 'aria-label': ariaLabel, 'aria-current': ariaCurrent } = props;
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          as={linkAs ?? `${basePath}${href}`}
          className={classes}
          aria-label={ariaLabel}
          aria-current={ariaCurrent}
          onClick={onClick}
          target={target}
          rel={rel}
        >
          {content}
        </Link>
      );
    }

    // Every prop this component OWNS has to come out before `...rest`, exactly as
    // Button.tsx does it. It used to pull only `disabled`/`type`/`aria-label`, which left
    // `className` in `rest` — and since `rest` is spread AFTER `className={classes}`, a
    // call site that passed a className silently replaced the whole DS class list (and
    // leaked `variant`/`color`/`size` into the DOM as invalid attributes). That is how a
    // "migrated to the DS Icon Button" control could still render with no DS styling.
    const {
      disabled,
      type,
      'aria-label': ariaLabel,
      size: _size,
      variant: _variant,
      color: _color,
      loading: _loading,
      children: _children,
      className: _className,
      decorative: _decorative,
      ...rest
    } = props as AsButton;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type ?? 'button'}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        aria-label={ariaLabel}
        {...rest}
      >
        {content}
      </button>
    );
  },
);
